import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, ChevronDown, KeyRound, LogOut, Eye, EyeOff, X, Shield, Phone, Mail, Building2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Helpers ─────────────────────────────────────────────────────────────────
function getInitials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AVATAR_COLORS = [
  'bg-blue-600', 'bg-violet-600', 'bg-emerald-600',
  'bg-amber-600', 'bg-rose-600', 'bg-cyan-600', 'bg-indigo-600',
];
function avatarColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatRole(role) {
  if (!role) return '';
  return role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-xs text-gray-500 font-ibm uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-800 font-ibm">{value}</p>
    </div>
  );
}

// ─── Profile Modal ────────────────────────────────────────────────────────────
function ProfileModal({ user, onClose }) {
  const initials = getInitials(user.full_name);
  const color    = avatarColor(user.full_name || '');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-700 px-6 py-8 flex flex-col items-center gap-3">
          <div className={`w-16 h-16 rounded-full ${color} flex items-center justify-center shadow-lg`}>
            <span className="text-white text-xl font-bold font-ibm">{initials}</span>
          </div>
          <div className="text-center">
            <h2 className="text-white font-bold font-ibm text-lg">{user.full_name || 'User'}</h2>
            <p className="text-gray-400 text-sm font-ibm">{user.email}</p>
            <span className="mt-1 inline-block text-xs px-2.5 py-0.5 bg-blue-500/20 text-blue-300 rounded-full font-ibm">
              {formatRole(user.role)}
            </span>
          </div>
          <button
            data-testid="profile-modal-close"
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Full Name"    value={user.full_name} />
            <Field label="Username"     value={user.username} />
            <Field label="Email"        value={user.email} />
            <Field label="Office Email" value={user.office_email} />
            <Field label="Phone"        value={user.phone} />
            <Field label="Alt Phone"    value={user.phone_secondary} />
            <Field label="Gender"       value={user.gender} />
            <Field label="Blood Group"  value={user.blood_group} />
            <Field label="Date of Joining" value={user.date_of_joining} />
            <Field label="PAN Number"   value={user.pan_number} />
          </div>
          {(user.address_line1 || user.city || user.state) && (
            <div className="pt-2 border-t border-gray-100">
              <Field
                label="Address"
                value={[user.address_line1, user.city, user.state, user.postal_code].filter(Boolean).join(', ')}
              />
            </div>
          )}
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-gray-900 text-white rounded-xl font-ibm text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Change Password Modal ────────────────────────────────────────────────────
function ChangePasswordModal({ onClose }) {
  const { toast } = useToast();
  const [form, setForm]     = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show, setShow]     = useState({ current: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast({ title: 'Error', description: 'New passwords do not match', variant: 'destructive' });
      return;
    }
    if (form.new_password.length < 6) {
      toast({ title: 'Error', description: 'New password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to change password');
      toast({ title: 'Password changed', description: 'Your password has been updated successfully.' });
      onClose();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const PasswordField = ({ id, label, fieldKey, showKey }) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-gray-700 font-ibm">{label}</label>
      <div className="relative">
        <input
          data-testid={`change-pw-${id}`}
          type={show[showKey] ? 'text' : 'password'}
          value={form[fieldKey]}
          onChange={e => setForm(prev => ({ ...prev, [fieldKey]: e.target.value }))}
          className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm font-ibm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={`Enter ${label.toLowerCase()}`}
          required
        />
        <button
          type="button"
          onClick={() => setShow(prev => ({ ...prev, [showKey]: !prev[showKey] }))}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {show[showKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-blue-600" />
            </div>
            <h2 className="font-bold font-ibm text-gray-900">Change Password</h2>
          </div>
          <button data-testid="change-pw-close" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <PasswordField id="current" label="Current Password" fieldKey="current_password" showKey="current" />
          <PasswordField id="new"     label="New Password"     fieldKey="new_password"     showKey="new" />
          <PasswordField id="confirm" label="Confirm Password" fieldKey="confirm_password" showKey="confirm" />

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-ibm text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="change-pw-submit"
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-ibm text-sm font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving…' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── UserMenu (main export) ───────────────────────────────────────────────────
const UserMenu = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [open, setOpen]               = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [user, setUser]               = useState(null);
  const dropdownRef                   = useRef(null);

  // Load user from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('user_profile');
      if (raw) setUser(JSON.parse(raw));
    } catch {}
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('company_profile');
    navigate('/');
  }, [navigate]);

  const name    = user?.full_name || 'User';
  const initials = getInitials(name);
  const color    = avatarColor(name);

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Avatar Button */}
        <button
          data-testid="user-profile-button"
          onClick={() => setOpen(v => !v)}
          className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 rounded-xl transition-colors"
        >
          <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
            <span className="text-white text-xs font-bold font-ibm">{initials}</span>
          </div>
          <div className="hidden md:block text-left">
            <p className="text-sm font-semibold text-gray-800 font-ibm leading-tight">{name}</p>
            <p className="text-xs text-gray-500 font-ibm">{formatRole(user?.role)}</p>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
            {/* User Info */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
                  <span className="text-white text-sm font-bold font-ibm">{initials}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 font-ibm truncate">{name}</p>
                  <p className="text-xs text-gray-500 font-ibm truncate">{user?.email}</p>
                  <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-ibm">
                    {formatRole(user?.role)}
                  </span>
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className="py-1.5">
              <button
                data-testid="menu-profile-btn"
                onClick={() => { setOpen(false); setShowProfile(true); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 font-ibm transition-colors"
              >
                <User className="w-4 h-4 text-gray-500" />
                My Profile
              </button>
              <button
                data-testid="menu-change-pw-btn"
                onClick={() => { setOpen(false); setShowChangePw(true); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 font-ibm transition-colors"
              >
                <KeyRound className="w-4 h-4 text-gray-500" />
                Change Password
              </button>
            </div>

            <div className="border-t border-gray-100 py-1.5">
              <button
                data-testid="menu-logout-btn"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 font-ibm transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>

      {showProfile  && user && <ProfileModal        user={user} onClose={() => setShowProfile(false)} />}
      {showChangePw &&         <ChangePasswordModal           onClose={() => setShowChangePw(false)} />}
    </>
  );
};

export default UserMenu;
